import { useEffect, useState } from "react";
import { api } from "../api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";
import { SignalCard, StatsGrid } from "../components/SignalCard";
import { TradesTable, EquityChart } from "../components/TradesTable";
import { PriceChart } from "../components/PriceChart";
import { GuardianSignalCard, GuardianOps } from "../components/Guardian";
import { Shield, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

export default function Dashboard() {
  const { user } = useAuth();
  const { lang, t } = useLang();
  const it = lang === "it";
  const [signal, setSignal] = useState(null);
  const [locked, setLocked] = useState(false);
  const [realtime, setRealtime] = useState(true);
  const [stats, setStats] = useState(null);
  const [equity, setEquity] = useState([]);
  const [tradesData, setTradesData] = useState({ trades: [], delay_business_days: 0 });
  const [gSignal, setGSignal] = useState(null);
  const guardianAccess = user?.guardian_access || user?.role === "admin";

  useEffect(() => {
    api.get("/signals/today")
      .then((r) => { setSignal(r.data.signal); setRealtime(r.data.realtime); })
      .catch(() => setLocked(true));
    api.get("/public/performance").then((r) => setStats(r.data)).catch(() => {});
    api.get("/public/equity").then((r) => setEquity(r.data)).catch(() => {});
    api.get("/trades").then((r) => setTradesData(r.data)).catch(() => {});
    if (guardianAccess) {
      api.get("/guardian/signals/today").then((r) => setGSignal(r.data.signal)).catch(() => {});
    }
  }, [guardianAccess]);

  const access = user?.access_level || "free";
  const tradesTitle = tradesData.delay_business_days === 0 ? t("live_trades_title")
    : tradesData.delay_business_days === 1 ? t("trial_trades_title") : t("public_trades_title");

  return (
    <div data-testid="dashboard-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-white">{t("dash_title")}</h1>
          <p className="text-sm text-slate-400 mt-1">{user?.email}</p>
        </div>
        <span data-testid="access-badge" className={`text-xs font-mono uppercase tracking-widest px-3 py-1.5 border ${
          access === "subscriber" || access === "admin" ? "border-emerald-500/40 text-emerald-400"
            : access === "trial" ? "border-amber-500/40 text-amber-400" : "border-white/15 text-slate-400"}`}>
          {t(`access_${access}`)}
        </span>
      </div>

      {access === "free" && (
        <div data-testid="upgrade-banner" className="border border-amber-500/30 bg-amber-500/5 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-amber-200">{t("upgrade_banner")}</p>
          <Link to="/pricing" data-testid="upgrade-banner-link" className="text-sm font-medium bg-emerald-500 hover:bg-emerald-400 text-black rounded px-4 py-1.5 transition-colors">{t("hero_cta")}</Link>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <SignalCard signal={signal} realtime={realtime} locked={locked} />
        <div className="lg:col-span-2"><EquityChart data={equity} /></div>
      </div>
      <PriceChart />
      <StatsGrid stats={stats} />
      <TradesTable trades={tradesData.trades} title={tradesTitle} />

      <div className="pt-6 border-t border-sky-500/15">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="font-heading text-xl font-bold text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-sky-400" /> Bund <span className="text-sky-400">Guardian</span>
          </h2>
          {guardianAccess ? (
            <span data-testid="guardian-access-badge" className="text-xs font-mono uppercase tracking-widest px-3 py-1.5 border border-sky-500/40 text-sky-400">
              {it ? "Abbonato Guardian" : "Guardian subscriber"}
            </span>
          ) : (
            <Link to="/guardian" data-testid="guardian-upgrade-link"
              className="inline-flex items-center gap-1 text-sm font-medium bg-sky-500 hover:bg-sky-400 text-black rounded px-4 py-1.5 transition-colors">
              {it ? "Scopri Guardian" : "Discover Guardian"} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          )}
        </div>
        {!guardianAccess && (
          <p className="text-sm text-slate-400 mb-4 max-w-2xl">
            {it
              ? "Il sistema conservativo che in 18 anni non ha mai chiuso un anno in perdita. L'operazione in corso è riservata agli abbonati Guardian (abbonamento separato, solo annuale)."
              : "The conservative system that never closed a losing year in 18 years. The ongoing operation is reserved for Guardian subscribers (separate subscription, annual only)."}
          </p>
        )}
        <div className="grid lg:grid-cols-3 gap-6">
          {guardianAccess && gSignal && <GuardianSignalCard signal={gSignal} />}
          <div className={guardianAccess && gSignal ? "lg:col-span-2" : "lg:col-span-3"}>
            <GuardianOps />
          </div>
        </div>
      </div>
    </div>
  );
}
